type Entry<T> = { value: T; expiresAt: number };

const store = new Map<string, Entry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

/**
 * Caché en memoria con TTL y deduplicación: llamadas concurrentes con la misma key comparten una promesa.
 */
export async function getOrSet<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const hit = store.get(key) as Entry<T> | undefined;
  if (hit && hit.expiresAt > now) {
    return hit.value;
  }

  const pending = inflight.get(key) as Promise<T> | undefined;
  if (pending) {
    return pending;
  }

  const p = (async () => {
    try {
      const value = await fetcher();
      store.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, p);
  return p;
}

/** Lectura síncrona del valor cacheado (aunque esté vencido). */
export function peekCacheValue<T>(key: string): T | undefined {
  const hit = store.get(key) as Entry<T> | undefined;
  return hit?.value;
}

export function isCacheKeyFresh(key: string, now = Date.now()): boolean {
  const hit = store.get(key);
  return !!hit && hit.expiresAt > now;
}

export function setCacheValue<T>(key: string, value: T, ttlMs: number): void {
  store.set(key, { value, expiresAt: Date.now() + ttlMs });
}

export function invalidateCacheKey(key: string): void {
  store.delete(key);
}

export function invalidateCacheKeyPrefix(prefix: string): void {
  for (const k of store.keys()) {
    if (k.startsWith(prefix)) {
      store.delete(k);
    }
  }
}
